/* eslint-disable */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useModal } from '../../hooks/use-modal-store'
import { Button } from '../ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import useMediaQuery from '../../hooks/useMediaQuery'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet'
import { Table, TableHeader, TableHead, TableBody, TableRow, TableCell } from '../ui/table'
import env from '../../env/env'
import { toCurrency } from '../../utils/CurrencyUtil'

const MIN_REDEMPTION_AMOUNT = 100
const SCROLLABLE_POPUP_CLASS = 'h-[80vh] max-h-[80vh] !flex !flex-col overflow-hidden'
const DEFAULT_POPUP_CLASS = 'max-h-[80vh] !flex !flex-col'
const REDEMPTION_DISCLAIMER =
  '*The amount credited may vary depending on the market value at the time of processing.'

const getMaxFundListHeight = () => window.innerHeight * 0.8 - 220

const FundListScrollArea = ({ onScrollableChange, children, scrollableClassName = '' }) => {
  const scrollRef = useRef(null)
  const [isScrollable, setIsScrollable] = useState(false)
  const [thumb, setThumb] = useState(null)

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current
    if (!el) return

    const shouldScroll = el.scrollHeight > getMaxFundListHeight()

    setIsScrollable((prev) => {
      if (prev !== shouldScroll) onScrollableChange?.(shouldScroll)
      return shouldScroll
    })

    if (!shouldScroll) {
      setThumb(null)
      return
    }

    const { scrollTop, scrollHeight, clientHeight } = el
    const thumbHeight = Math.max((clientHeight / scrollHeight) * clientHeight, 32)
    const maxScrollTop = scrollHeight - clientHeight
    const maxThumbTop = clientHeight - thumbHeight
    const thumbTop = maxScrollTop > 0 ? (scrollTop / maxScrollTop) * maxThumbTop : 0

    setThumb({ height: thumbHeight, top: thumbTop })
  }, [onScrollableChange])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    updateScrollState()
    el.addEventListener('scroll', updateScrollState)

    const resizeObserver = new ResizeObserver(updateScrollState)
    resizeObserver.observe(el)
    if (el.firstElementChild) resizeObserver.observe(el.firstElementChild)
    window.addEventListener('resize', updateScrollState)

    return () => {
      el.removeEventListener('scroll', updateScrollState)
      resizeObserver.disconnect()
      window.removeEventListener('resize', updateScrollState)
    }
  }, [updateScrollState, children])

  return (
    <div
      className={`relative ${isScrollable ? scrollableClassName || 'h-[calc(80vh-220px)] min-h-0' : ''}`}
    >
      {isScrollable && thumb && (
        <div className='pointer-events-none absolute right-0 top-0 bottom-0 z-10 w-1 rounded-full bg-[#f4f7fe]'>
          <div
            className='absolute right-0 w-1 rounded-full bg-[#9aa8c4]'
            style={{ height: thumb.height, top: thumb.top }}
          />
        </div>
      )}
      <div
        ref={scrollRef}
        className={`pr-2 ${isScrollable ? 'mf-redeem-scrollbar-hidden h-full overflow-y-scroll' : ''}`}
        onScroll={isScrollable ? updateScrollState : undefined}
      >
        {children}
      </div>
    </div>
  )
}

const getFundRedemptionAmount = (fund) => {
  if (fund.redemptionType === 'VALUE') {
    return fund.withdrawableAmount ?? 0
  }

  if (fund.redemptionType === 'UNITS') {
    const units = fund.withdrawableUnits ?? fund.qty ?? 0
    return units * (fund.nav ?? 0)
  }

  return fund.currentValue ?? (fund.units ?? 0) * (fund.nav ?? 0)
}

const getFundRedemptionLabel = (fund) => {
  if (fund.redemptionType === 'ALL_UNITS') {
    return 'Full redemption'
  }

  if (fund.redemptionType === 'UNITS') {
    const units = fund.withdrawableUnits ?? fund.qty
    return units ? `${units} units` : null
  }

  return null
}

const MFRedeemBreakdown = () => {
  const { isOpen, onClose, type, data } = useModal()
  const isModalOpen = isOpen && type === 'openMFRedeemBreakdown'
  const isDesktop = useMediaQuery('(min-width: 1024px)')
  const [isLoading, setIsLoading] = useState(false)
  const [needsScroll, setNeedsScroll] = useState(false)

  const totalAmount =
    data?.amount ??
    data?.constituents?.reduce((acc, fund) => acc + getFundRedemptionAmount(fund), 0)

  const hasLowRedemptionAmount = data?.constituents?.some(
    (fund) => Math.floor(getFundRedemptionAmount(fund)) < MIN_REDEMPTION_AMOUNT,
  )

  useEffect(() => {
    if (!isModalOpen) setNeedsScroll(false)
  }, [isModalOpen])

  const popupContainerClass = needsScroll ? SCROLLABLE_POPUP_CLASS : DEFAULT_POPUP_CLASS

  const handleContinue = async () => {
    if (!data?.onContinue || hasLowRedemptionAmount) return

    try {
      setIsLoading(true)
      await data.onContinue()
      onClose()
    } finally {
      setIsLoading(false)
    }
  }

  const renderFundRow = (fund, index, isMobile = false) => {
    const redemptionLabel = getFundRedemptionLabel(fund)
    const redemptionAmount = getFundRedemptionAmount(fund)

    if (isMobile) {
      return (
        <TableRow key={index} className='border-none'>
          <TableCell className='font-medium flex gap-x-2 px-4'>
            <img
              src={`${env.MF_LOGOS_URL}/${fund?.amcCode}.png`}
              alt={`${fund.amcCode} icon`}
              className='rounded-md h-12 w-12'
            />
            <div className='w-full'>
              <p className='font-semibold text-sm text-nowrap truncate max-w-[284px]'>
                {fund.schemeName}
              </p>
              <div className='flex justify-between'>
                {/* <span className='font-medium text-xs text-black-400'>
                  {redemptionLabel || fund.redemptionType}
                </span> */}
                <span className='font-semibold text-right text-sm'>
                  ₹ {toCurrency(Math.floor(redemptionAmount))}
                </span>
              </div>
            </div>
          </TableCell>
        </TableRow>
      )
    }

    return (
      <TableRow key={index}>
        <TableCell className='font-medium flex items-center gap-x-2 max-w-[432px]'>
          <img
            src={`${env.MF_LOGOS_URL}/${fund?.amcCode}.png`}
            alt={`${fund.amcCode} icon`}
            className='rounded-md h-12 w-12'
          />
          <div>
            <p className='font-semibold text-base text-nowrap truncate max-w-[400px]'>
              {fund.schemeName}
            </p>
            {/* <span className='font-medium text-xs text-black-400'>
              {redemptionLabel || fund.redemptionType}
            </span> */}
          </div>
        </TableCell>
        <TableCell className='font-semibold text-right'>
          ₹ {toCurrency(Math.floor(redemptionAmount))}
        </TableCell>
      </TableRow>
    )
  }

  const renderMinAmountError = (className = '') =>
    hasLowRedemptionAmount ? (
      <p className={`text-sm text-error-500 font-medium ${className}`}>
        Minimum redemption amount per fund should be ₹{MIN_REDEMPTION_AMOUNT}
      </p>
    ) : null

  return (
    <>
      {isDesktop ? (
        <Dialog open={isModalOpen} onOpenChange={() => onClose()}>
          <DialogContent
            className={`sm:max-w-[736px] font-DMSans p-6 gap-4 ${popupContainerClass}`}
            showclose={true}
          >
            <DialogHeader className='shrink-0'>
              <DialogTitle className='text-start font-semibold text-xl text-[#2D2D2D]'>
                Redemption Breakdown
              </DialogTitle>
              <DialogDescription className='text-start text-black-400 text-sm font-DMSans font-medium'>
                This is how your withdrawal of{' '}
                <b className='text-black-500'>₹ {toCurrency(Math.floor(totalAmount))}</b> gets
                divided
              </DialogDescription>
            </DialogHeader>
            <FundListScrollArea onScrollableChange={setNeedsScroll}>
              <Table>
                <TableHeader className='bg-[#F4F7FE]'>
                  <TableRow className='border-none'>
                    <TableHead className='text-primary-400 w-[50%] pl-12 rounded-l-xl'>
                      Fund Name
                    </TableHead>
                    <TableHead className='text-primary-400 text-right rounded-r-xl'>
                      Amount
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.constituents?.map((fund, index) => renderFundRow(fund, index))}
                </TableBody>
              </Table>
              {renderMinAmountError('mt-3')}
            </FundListScrollArea>
            <DialogFooter className='shrink-0 flex-row items-center justify-between gap-4 w-full sm:justify-between'>
              <p className='text-xs text-black-400 font-medium text-left max-w-[70%]'>
                {REDEMPTION_DISCLAIMER}
              </p>
              <Button
                className='rounded-lg bg-primary-500 hover:bg-primary-400 disabled:bg-primary-200 w-full sm:w-auto'
                onClick={handleContinue}
                loading={isLoading}
                disabled={hasLowRedemptionAmount}
              >
                Confirm
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : (
        <Sheet open={isModalOpen} onOpenChange={() => onClose()}>
          <SheetContent
            className={`font-DMSans p-0 py-5 rounded-t-[24px] bottom-0 gap-0 ${popupContainerClass}`}
            showClose={true}
            side='bottom'
          >
            <SheetHeader className='px-4 shrink-0'>
              <SheetTitle className='text-start font-semibold text-[#2D2D2D]'>
                Redemption Breakdown
              </SheetTitle>
              <SheetDescription className='text-start text-black-400 font-medium text-sm'>
                This is how your withdrawal of{' '}
                <b className='text-black-500'>₹ {toCurrency(Math.floor(totalAmount))}</b> gets
                divided
              </SheetDescription>
            </SheetHeader>

            <FundListScrollArea
              onScrollableChange={setNeedsScroll}
              scrollableClassName='flex-1 min-h-0'
            >
              <Table>
                <TableBody>
                  {data?.constituents?.map((fund, index) => renderFundRow(fund, index, true))}
                </TableBody>
              </Table>

              {renderMinAmountError('px-4 mt-3')}
            </FundListScrollArea>

            <div className='px-4 pt-4 pb-2 shrink-0'>
              <p className='text-xs text-black-400 font-medium mb-3'>{REDEMPTION_DISCLAIMER}</p>
              <Button
                className='rounded-lg bg-primary-500 hover:bg-primary-400 disabled:bg-primary-200 w-full'
                onClick={handleContinue}
                loading={isLoading}
                disabled={hasLowRedemptionAmount}
              >
                Confirm
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      )}
    </>
  )
}

export default MFRedeemBreakdown
